import { type FC } from 'react';
import { type LucideIcon, LucideX } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export interface NodeProps {
  path: string;
  icon?: LucideIcon;
  iconClassName?: string;
  onRemove?: () => void;
  disabled?: boolean;
  className?: string;
}

export const Node: FC<NodeProps> = ({ path, icon: Icon, iconClassName, onRemove, disabled, className }) => {
  const tCommon = useTranslations('common');

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {Icon && (
        <Icon className={cn('icon-6', iconClassName, { 'opacity-25': disabled })} strokeWidth={2} data-cy="node-icon" />
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn('line-clamp-1 w-full break-all text-xs font-semibold text-text-gray wrap-anywhere', {
              'opacity-25': disabled,
            })}
            data-cy="node-path"
          >
            {path}
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent className="max-w-96 wrap-anywhere" data-cy="node-tooltip-content">
            {path}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>

      {onRemove && (
        <Button
          type="button"
          variant="main-toolbar"
          size="secondary-toolbar"
          className="p-0 hover:[&>svg]:stroke-icon-base"
          onClick={onRemove}
          disabled={disabled}
          data-cy="remove-node-button"
          icon={LucideX}
          iconClassName="icon-5"
          tooltip={tCommon('remove')}
        />
      )}
    </div>
  );
};
